// <gift-details>: the buyer finds their order, then adds the bubble message for each gift and (when missing)
// pickup/delivery, day and time. Saved to the order by the shop service (/order-details/*).

import { buildCalendar } from '@theme/dates';
import { loadBooked, dateChips, slotChips } from '@theme/calendar-ui';
import { checkEmail, checkPostcode, parseZones } from '@theme/validate';
import { bubbleSvg } from '@theme/bubble';
import { $, $$, escapeHtml, formatPrice } from '@theme/utils';

const MAX_MESSAGE = 24;
const service = (path) => `${(window.theme?.slotsServiceUrl || '').replace(/\/$/, '')}${path}`;

async function post(path, body) {
  const response = await fetch(service(path), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok && data.ok, data };
}

class GiftDetails extends HTMLElement {
  connectedCallback() {
    this.find = $('[data-step="find"]', this);
    this.details = $('[data-step="details"]', this);
    this.state = { fulfilment: '', date: '', time: '' };
    this.zones = parseZones(window.theme?.delivery?.zones);
    const fromLink = new URLSearchParams(window.location.search).get('order');
    if (fromLink) $('[name="order"]', this.find).value = fromLink;
    this.find.addEventListener('submit', (e) => this.lookup(e));
    this.details.addEventListener('submit', (e) => this.submit(e));
    this.details.addEventListener('click', (e) => this.onPick(e));
    this.details.addEventListener('change', (e) => this.onChange(e));
    this.details.addEventListener('input', (e) => this.onInput(e));
  }

  showErrors(form, errors = {}) {
    $$('[data-error]', form).forEach((el) => { el.textContent = errors[el.dataset.error] || ''; });
  }

  busy(form, on) {
    const button = $('[type="submit"]', form);
    button.classList.toggle('is-loading', on);
    button.disabled = on;
  }

  async lookup(event) {
    event.preventDefault();
    const order = $('[name="order"]', this.find).value.trim();
    const email = checkEmail($('[name="email"]', this.find).value);
    if (!order || !email.ok) {
      this.showErrors(this.find, { form: !order ? 'Add your order number. It’s in your confirmation email.' : email.message });
      return;
    }
    this.busy(this.find, true);
    const { ok, data } = await post('/order-details/lookup', { order, email: email.value }).catch(() => ({ ok: false, data: {} }));
    this.busy(this.find, false);
    if (!ok) {
      this.showErrors(this.find, data.errors || { form: 'We couldn’t look that up just now. Please try again, or call us.' });
      return;
    }
    this.showErrors(this.find);
    this.credentials = { order, email: email.value };
    this.order = data.order;
    if (this.order.complete) return this.done('Your gift details are already on the order. Nothing more to do.');
    this.renderOrder();
  }

  renderOrder() {
    const o = this.order;
    $('[data-summary]', this).textContent = `Order ${o.name}: ${o.lines.map((l) => `${l.quantity} × ${l.title}`).join(', ')}`;
    $('[data-lines]', this).innerHTML = o.lines.filter((l) => l.needsMessage).map((l) => `
      <div class="gd-line" data-line="${escapeHtml(l.id)}">
        <div class="gd-line__preview" data-preview>${bubbleSvg({ text: l.message || '' })}</div>
        <div>
          <label class="label" for="gd-${escapeHtml(l.id)}">Message for ${escapeHtml(l.title)}${l.variantTitle ? ` (${escapeHtml(l.variantTitle)})` : ''}</label>
          <div class="field"><input id="gd-${escapeHtml(l.id)}" maxlength="${MAX_MESSAGE}" value="${escapeHtml(l.message || '')}" placeholder="Happy 25th, Mum & Dad" data-message></div>
          <p class="hint is-error" data-error="line:${escapeHtml(l.id)}"></p>
        </div>
      </div>`).join('');

    const when = $('[data-when]', this);
    when.hidden = !o.needsDay;
    if (o.needsDay) {
      this.state = { fulfilment: o.fulfilment || '', date: o.date || '', time: o.time || '' };
      const radio = $(`[name="fulfilment"][value="${this.state.fulfilment}"]`, this);
      if (radio) radio.checked = true;
      this.toggleDelivery();
      this.days = buildCalendar(window.theme?.delivery || {});
      this.renderCalendar();
      loadBooked(this.days).then((booked) => { this.days = buildCalendar(window.theme?.delivery || {}, booked); this.renderCalendar(); });
    }
    this.find.hidden = true;
    this.details.hidden = false;
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  renderCalendar() {
    if (this.state.date && !this.days.some((d) => d.iso === this.state.date && !d.disabled)) this.state = { ...this.state, date: '', time: '' };
    $('[data-dates]', this).innerHTML = dateChips(this.days, this.state.date);
    $('[data-slots]', this).innerHTML = slotChips(this.days.find((d) => d.iso === this.state.date), this.state.time);
  }

  onPick(event) {
    const date = event.target.closest('[data-date]');
    const slot = event.target.closest('[data-slot]');
    if (date && !date.disabled) this.state = { ...this.state, date: date.dataset.date, time: '' };
    if (slot && !slot.disabled) this.state = { ...this.state, time: slot.dataset.slot };
    if (date || slot) this.renderCalendar();
  }

  onChange(event) {
    if (event.target.name === 'fulfilment') {
      this.state.fulfilment = event.target.value;
      this.toggleDelivery();
    }
  }

  onInput(event) {
    if (event.target.matches('[data-message]')) {
      const preview = $('[data-preview]', event.target.closest('[data-line]'));
      preview.innerHTML = bubbleSvg({ text: event.target.value });
    }
    if (event.target.name === 'postcode') {
      const postcode = checkPostcode(event.target.value).value.slice(0, 4);
      event.target.value = postcode;
      const zone = this.zones.find((z) => z.postcode === postcode);
      const hint = $('[data-zone]', this);
      hint.className = postcode.length < 4 ? 'hint' : zone ? 'hint is-ok' : 'hint is-error';
      hint.textContent = postcode.length < 4 ? '' : zone ? `✓ We deliver to ${zone.suburbs || postcode} (${formatPrice(zone.fee)}).` : `We don't deliver to ${postcode} yet. Choose pickup, or call us.`;
    }
  }

  toggleDelivery() {
    $$('[data-delivery-only]', this).forEach((el) => { el.hidden = this.state.fulfilment !== 'Delivery'; });
  }

  collect() {
    const errors = {};
    const lines = $$('[data-line]', this).map((row) => {
      const message = $('[data-message]', row).value.trim();
      if (!message) errors[`line:${row.dataset.line}`] = 'Add the message for this gift.';
      return { id: row.dataset.line, message };
    });
    const body = { ...this.credentials, lines };
    if (this.order.needsDay) {
      const postcode = $('[name="postcode"]', this).value.trim();
      Object.assign(body, { fulfilment: this.state.fulfilment, date: this.state.date, time: this.state.time, postcode, notes: $('[name="notes"]', this).value.trim() });
      if (!body.fulfilment) errors.fulfilment = 'Choose pickup or delivery.';
      if (!body.date) errors.date = 'Choose a day.';
      else if (!body.time) errors.time = 'Choose a time.';
      if (body.fulfilment === 'Delivery' && !this.zones.some((z) => z.postcode === postcode)) errors.postcode = 'Enter a postcode we deliver to.';
    }
    return { body, errors };
  }

  async submit(event) {
    event.preventDefault();
    const { body, errors } = this.collect();
    if (errors.postcode) $('[data-zone]', this).textContent = errors.postcode;
    this.showErrors(this.details, errors);
    if (Object.keys(errors).length) return;
    this.busy(this.details, true);
    const { ok, data } = await post('/order-details/submit', body).catch(() => ({ ok: false, data: {} }));
    this.busy(this.details, false);
    if (!ok) {
      this.showErrors(this.details, data.errors || { form: 'We couldn’t save that just now. Please try again, or call us.' });
      return;
    }
    this.done();
  }

  done(text) {
    if (text) $('[data-done-text]', this).textContent = text;
    this.find.hidden = true;
    this.details.hidden = true;
    $('[data-step="done"]', this).hidden = false;
  }
}

customElements.define('gift-details', GiftDetails);
